<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Eye, EyeOff } from 'lucide-vue-next';
import CopyValueButton from '@/components/record/fields/CopyValueButton.vue';
import { PASSWORD_MASK } from '@/components/record/fields/fieldKind';

/**
 * A login password, drawn as a password field wherever it shows: in Details,
 * in a table cell and on a card.
 *
 * - It shows eight dots until the eye button is pressed, whatever the
 *   password's length.
 * - The eye button shows the password, and pressing it again hides it. It is
 *   named "Show password" or "Hide password".
 * - Revealing applies to this one value. It hides again when the record
 *   changes, but not when the same record refreshes.
 * - While it is revealed, a copy button sits beside it, as it does beside a
 *   code in Details.
 * - With no password it draws nothing, not even the button. The API sends the
 *   readable password only to someone who may edit the contact login, so
 *   everyone else gets no value. Details then shows its usual dash.
 *
 * The buttons stop their clicks, so pressing one never also selects the row
 * or the card it sits in.
 */
const props = defineProps<{
    value: unknown;
    /** The field's name, for the copy button's "Copy Password". */
    label: string;
    /** The record's id. A different record hides the password again. */
    recordKey?: string | number | null;
}>();

const text = computed(() => (props.value === null || props.value === undefined ? '' : String(props.value)));
const revealed = ref(false);

watch(
    () => props.recordKey,
    () => {
        revealed.value = false;
    },
);

function toggleReveal(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    revealed.value = !revealed.value;
}
</script>

<template>
  <span v-if="text !== ''" class="group/field inline-flex max-w-full min-w-0 items-center gap-1" data-testid="password-value">
    <span v-if="revealed" class="min-w-0 font-mono text-[13px] [overflow-wrap:anywhere]" data-testid="password-text">{{ text }}</span>
    <template v-else>
      <span aria-hidden="true" class="tracking-wider" data-testid="password-mask">{{ PASSWORD_MASK }}</span>
      <span class="sr-only">Hidden</span>
    </template>
    <button
      type="button"
      :aria-label="revealed ? 'Hide password' : 'Show password'"
      :title="revealed ? 'Hide password' : 'Show password'"
      data-testid="password-reveal"
      class="inline-flex size-5 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none"
      @click="toggleReveal"
      @keydown.stop
    >
      <EyeOff v-if="revealed" class="size-3.5" aria-hidden="true" />
      <Eye v-else class="size-3.5" aria-hidden="true" />
    </button>
    <CopyValueButton v-if="revealed" :text="text" :label="label" />
  </span>
</template>
